import axios from 'axios';
import {
    AuthResponse,
    LoginRequest,
    RegisterRequest,
    Form,
    FormCreateRequest,
    FormUpdateRequest,
    PaginatedFormSubmissions,
    PaginatedForms,
    PaginatedUsers,
    PipedriveProduct,
    PipedriveProductVariation,
    PipedriveDealField,
    User,
    UserCreateRequest,
    UserUpdateRequest,
    PasswordChangeRequest,
    SyncResult,
    WooCommerceOrder,
    PaginatedWooCommerceOrders
} from '../types';
import { prepareFormDataFromAPI } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // ⚠️ CRITICAL: Send cookies for refresh token
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Token refresh mutex to prevent race conditions
let refreshPromise: Promise<string> | null = null;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Response interceptor to handle auth errors with token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried refreshing yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Don't try to refresh on login or refresh endpoints
            if (originalRequest.url?.includes('/token') || originalRequest.url?.includes('/refresh')) {
                // Login or refresh failed, check for token reuse detection
                const detail = error.response?.data?.detail || '';
                if (detail.toLowerCase().includes('token reuse')) {
                    console.error('🚨 Security breach detected: Token reuse');
                    alert('Security alert: Your session has been terminated due to suspicious activity. Please login again.');
                }

                // Redirect to login
                if (window.location.pathname !== '/login') {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }

            // If refresh already in progress, wait for it
            if (refreshPromise) {
                try {
                    const token = await refreshPromise;
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                } catch (err) {
                    return Promise.reject(err);
                }
            }

            originalRequest._retry = true;

            // Start new refresh (mutex pattern)
            refreshPromise = (async () => {
                try {
                    // Call refresh endpoint (refresh token sent automatically as httpOnly cookie)
                    const response = await api.post('/refresh');
                    const { access_token, user } = response.data;

                    // Update stored token and user
                    localStorage.setItem('token', access_token);
                    localStorage.setItem('user', JSON.stringify(user));

                    // Update the authorization header
                    api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
                    originalRequest.headers.Authorization = `Bearer ${access_token}`;

                    // Process queued requests
                    processQueue(null, access_token);

                    return access_token;

                } catch (refreshError: any) {
                    // Refresh failed - check if it's token reuse detection
                    const detail = refreshError.response?.data?.detail || '';
                    if (detail.toLowerCase().includes('token reuse')) {
                        console.error('🚨 Security breach detected: Token reuse');
                        alert('Security alert: Your session has been terminated due to suspicious activity. Please login again.');
                    }

                    // Process queued requests with error
                    processQueue(refreshError, null);

                    // Clear auth data and redirect to login
                    if (window.location.pathname !== '/login') {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        window.location.href = '/login';
                    }

                    throw refreshError;
                } finally {
                    refreshPromise = null;
                }
            })();

            try {
                const token = await refreshPromise;
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
            } catch (err) {
                return Promise.reject(err);
            }
        }

        return Promise.reject(error);
    }
);

export const authApi = {
    login: async (credentials: LoginRequest): Promise<AuthResponse> => {
        const formData = new FormData();
        formData.append('username', credentials.username);
        formData.append('password', credentials.password);

        const response = await api.post('/token', formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        return response.data;
    },

    register: async (userData: RegisterRequest): Promise<AuthResponse> => {
        const response = await api.post('/users/', userData);
        return response.data;
    },

    getCurrentUser: async () => {
        const response = await api.get('/users/me');
        return response.data;
    },

    logout: async (): Promise<void> => {
        // Call backend to clear httpOnly cookie
        await api.post('/logout');
    },

    refresh: async (): Promise<AuthResponse> => {
        const response = await api.post('/refresh');
        return response.data;
    },
};

export const userApi = {
    // User self-management
    updateProfile: async (userData: UserUpdateRequest): Promise<User> => {
        const response = await api.put('/users/me', userData);
        return response.data;
    },

    changePassword: async (passwordData: PasswordChangeRequest): Promise<void> => {
        await api.put('/users/me/password', passwordData);
    },

    // Admin user management
    getAllUsers: async (
        skip = 0,
        limit = 100,
        orderBy: string = 'created_at',
        orderDirection: string = 'desc'
    ): Promise<PaginatedUsers> => {
        const response = await api.get('/admin/users', {
            params: { skip, limit, order_by: orderBy, order_direction: orderDirection }
        });
        return response.data;
    },

    getUserById: async (id: number): Promise<User> => {
        const response = await api.get(`/admin/users/${id}`);
        return response.data;
    },

    createUser: async (userData: UserCreateRequest): Promise<User> => {
        const response = await api.post('/admin/users', userData);
        return response.data;
    },

    updateUser: async (id: number, userData: UserUpdateRequest): Promise<User> => {
        const response = await api.put(`/admin/users/${id}`, userData);
        return response.data;
    },

    activateUser: async (id: number): Promise<void> => {
        await api.post(`/admin/users/${id}/activate`);
    },

    deactivateUser: async (id: number): Promise<void> => {
        await api.post(`/admin/users/${id}/deactivate`);
    },

    deleteUser: async (id: number): Promise<void> => {
        await api.delete(`/admin/users/${id}`);
    },

    changeUserPassword: async (id: number, passwordData: PasswordChangeRequest): Promise<void> => {
        await api.put(`/admin/users/${id}/password`, passwordData);
    },
};

export const formsApi = {
    getForms: async (
        skip: number = 0,
        limit: number = 100,
        orderBy: string = 'created_at',
        orderDirection: string = 'desc',
        includeStats: boolean = true
    ): Promise<PaginatedForms> => {
        const response = await api.get('/forms/', {
            params: { 
                skip, 
                limit, 
                order_by: orderBy, 
                order_direction: orderDirection,
                include_stats: includeStats 
            }
        });
        return {
            ...response.data,
            items: response.data.items.map(prepareFormDataFromAPI)
        };
    },

    getForm: async (id: number): Promise<Form> => {
        const response = await api.get(`/forms/${id}`);
        return prepareFormDataFromAPI(response.data);
    },

    createForm: async (formData: FormCreateRequest): Promise<Form> => {
        const response = await api.post('/forms/', formData);
        return prepareFormDataFromAPI(response.data);
    },

    updateForm: async (id: number, formData: FormUpdateRequest): Promise<Form> => {
        const response = await api.put(`/forms/${id}`, formData);
        return prepareFormDataFromAPI(response.data);
    },

    deleteForm: async (id: number): Promise<void> => {
        await api.delete(`/forms/${id}`);
    },

    getFormSubmissions: async (
        formId: number,
        skip: number = 0,
        limit: number = 10,
        orderBy: string = 'submitted_at',
        orderDirection: string = 'desc'
    ): Promise<PaginatedFormSubmissions> => {
        const response = await api.get(`/forms/${formId}/submissions`, {
            params: { skip, limit, order_by: orderBy, order_direction: orderDirection }
        });
        return response.data;
    },

    getFormPreview: async (formData: FormCreateRequest): Promise<string> => {
        const response = await api.post('/forms/preview', formData, {
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: 'text'
        });
        return response.data;
    },

    getFormTestHtml: async (formId: number): Promise<string> => {
        const response = await api.get(`/forms/${formId}/test`, {
            responseType: 'text'
        });
        return response.data;
    },

    syncFormSubmissionsPaymentStatus: async (formId: number): Promise<SyncResult> => {
        const response = await api.post(`/forms/${formId}/submissions/sync-payment-status`);
        return response.data;
    },
};

export const pipedriveApi = {
    searchProducts: async (name?: string): Promise<PipedriveProduct[]> => {
        const params = name ? { name } : {};
        const response = await api.get('/pipedrive/products', { params });
        return response.data;
    },

    getProduct: async (id: number): Promise<PipedriveProduct> => {
        const response = await api.get(`/pipedrive/products/${id}`);
        return response.data;
    },

    getProductVariations: async (productId: number): Promise<PipedriveProductVariation[]> => {
        const response = await api.get(`/pipedrive/products/${productId}/variations`);
        return response.data;
    },

    getDealFields: async (): Promise<PipedriveDealField[]> => {
        const response = await api.get('/pipedrive/deal-fields');
        return response.data;
    },
};

export const woocommerceApi = {
    getOrders: async (
        skip: number = 0,
        limit: number = 100,
        orderBy: string = 'received_at',
        orderDirection: string = 'desc'
    ): Promise<PaginatedWooCommerceOrders> => {
        const response = await api.get('/woocommerce/orders', {
            params: { 
                skip, 
                limit, 
                order_by: orderBy, 
                order_direction: orderDirection
            }
        });
        return response.data;
    },

    getOrder: async (id: number): Promise<WooCommerceOrder> => {
        const response = await api.get(`/woocommerce/orders/${id}`);
        return response.data;
    },

    syncOrdersDeals: async (): Promise<SyncResult> => {
        const response = await api.post('/woocommerce/orders/sync-deals');
        return response.data;
    },
};

export default api;
